import { Lock } from "lucide-react";

/*
 * App-preview mockups. To replace any mockup with a real DriveClear screenshot later,
 * swap the inner content for <img src="/screens/<name>.png" alt="..." className="w-full h-auto block" />.
 * Each mock is labelled as an app preview — these are not website features.
 */

export function PhoneMock({ title, children, testid, wide = false }) {
  return (
    <div data-testid={testid} className={`${wide ? "w-full max-w-sm" : "w-44 sm:w-52"} mx-auto rounded-[2rem] bg-white border border-stone-200 p-2 shadow-xl shadow-stone-900/10`}>
      <div className="rounded-[1.6rem] border border-stone-100 bg-white overflow-hidden">
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-stone-100">
          <span className="text-[10px] font-bold text-stone-800">{title}</span>
          <span className="text-[8px] font-semibold uppercase tracking-wide text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">App preview</span>
        </div>
        <div className="p-3">{children}</div>
      </div>
    </div>
  );
}

const Row = ({ label, value, accent = false }) => (
  <div className="flex items-center justify-between py-1.5">
    <span className="text-[10px] text-stone-500">{label}</span>
    <span className={`text-[11px] font-bold ${accent ? "text-emerald-600" : "text-stone-900"}`}>{value}</span>
  </div>
);

const Bar = ({ pct, color = "bg-blue-600" }) => (
  <div className="h-1.5 rounded-full bg-stone-100 overflow-hidden">
    <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
  </div>
);

const LockedRow = ({ label }) => (
  <div className="flex items-center justify-between py-1.5 text-stone-400">
    <span className="text-[10px] flex items-center gap-1.5"><Lock className="w-3 h-3" /> {label}</span>
    <span className="text-[9px] font-semibold uppercase tracking-wide">In app</span>
  </div>
);

export function FitPreviewMock({ score = 79, label = "Strong", rows = [] }) {
  const locked = rows.length ? rows.slice(0, 5) : ["What's helping", "What we'd watch", "Underwriter view", "Why this answer", "Best next move"];
  return (
    <PhoneMock title="Fit Analysis" testid="mock-fit-preview" wide>
      <div className="flex items-center gap-3 mb-2">
        <div className="relative w-14 h-14">
          <svg viewBox="0 0 40 40" className="w-14 h-14 -rotate-90">
            <circle cx="20" cy="20" r="16" fill="none" stroke="#e7e5e4" strokeWidth="5" />
            <circle cx="20" cy="20" r="16" fill="none" stroke="#10b981" strokeWidth="5" strokeLinecap="round" strokeDasharray={`${2 * Math.PI * 16}`} strokeDashoffset={`${2 * Math.PI * 16 * (1 - score / 100)}`} />
          </svg>
          <span className="absolute inset-0 flex items-center justify-center text-[11px] font-extrabold text-stone-900">{score}%</span>
        </div>
        <div>
          <div className="text-[11px] font-bold text-emerald-600 flex items-center gap-1">✓ {score}% {label}</div>
          <div className="text-[9px] text-stone-500">Your DriveClear Fit</div>
        </div>
      </div>
      <div className="divide-y divide-stone-100">
        {locked.map((r) => <LockedRow key={r} label={r} />)}
      </div>
    </PhoneMock>
  );
}

export function PathMock() {
  return (
    <PhoneMock title="Path to Purchase" testid="mock-path-to-purchase" wide>
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="text-[9px] text-stone-500">Current Fit</div>
          <div className="text-base font-extrabold text-amber-500">58%</div>
        </div>
        <div className="text-stone-300 text-sm">→</div>
        <div className="text-right">
          <div className="text-[9px] text-stone-500">Target Fit</div>
          <div className="text-base font-extrabold text-emerald-600">80%</div>
        </div>
      </div>
      <Bar pct={58} color="bg-amber-400" />
      <div className="mt-1 mb-2"><Bar pct={80} color="bg-emerald-500" /></div>
      <div className="text-[9px] font-semibold uppercase tracking-wide text-stone-400 mb-1">Example adjustments</div>
      <div className="divide-y divide-stone-100">
        <Row label="Down payment" value="+$3,200" accent />
        <Row label="Monthly debt" value="−$175" accent />
        <Row label="Vehicle price" value="−$1,500" accent />
      </div>
    </PhoneMock>
  );
}

export function GarageMock() {
  return (
    <PhoneMock title="Dream Garage" testid="mock-dream-garage" wide>
      <div className="rounded-xl border border-stone-100 bg-stone-50 p-3 mb-2">
        <div className="h-14 rounded-lg bg-gradient-to-br from-stone-200 to-stone-100 flex items-center justify-center text-stone-400 text-[10px] font-semibold mb-2">
          2026 BMW M3
        </div>
        <div className="flex items-center justify-between">
          <span className="text-[10px] text-stone-500">Current Fit <strong className="text-amber-500">61%</strong></span>
          <span className="text-[10px] text-stone-500">Target <strong className="text-emerald-600">80%</strong></span>
        </div>
      </div>
      <div className="space-y-2">
        <div>
          <div className="flex justify-between text-[9px] text-stone-500 mb-1"><span>Savings progress</span><span>62%</span></div>
          <Bar pct={62} />
        </div>
        <div>
          <div className="flex justify-between text-[9px] text-stone-500 mb-1"><span>Down-payment goal</span><span>$6,200 / $10,000</span></div>
          <Bar pct={62} color="bg-emerald-500" />
        </div>
        <div>
          <div className="flex justify-between text-[9px] text-stone-500 mb-1"><span>Progress toward target</span><span>On track</span></div>
          <Bar pct={45} color="bg-stone-400" />
        </div>
      </div>
    </PhoneMock>
  );
}

export function MiniFitMock() {
  return (
    <PhoneMock title="Fit Check" testid="mock-mini-fit">
      <div className="flex flex-col items-center py-2">
        <div className="w-16 h-16 rounded-full border-[6px] border-emerald-500 flex items-center justify-center">
          <span className="text-sm font-extrabold text-stone-900">79%</span>
        </div>
        <span className="text-[10px] font-bold text-emerald-600 mt-1.5">Strong</span>
        <div className="w-full mt-2 space-y-1">
          <div className="h-1.5 rounded bg-stone-100" />
          <div className="h-1.5 rounded bg-stone-100 w-4/5" />
        </div>
      </div>
    </PhoneMock>
  );
}

export function MiniPathMock() {
  return (
    <PhoneMock title="Path to Purchase" testid="mock-mini-path">
      <div className="py-2 space-y-2">
        <div className="flex justify-between text-[10px] font-bold"><span className="text-amber-500">58%</span><span className="text-emerald-600">80%</span></div>
        <Bar pct={58} color="bg-amber-400" />
        <Bar pct={80} color="bg-emerald-500" />
        <div className="space-y-1 pt-1">
          <div className="h-1.5 rounded bg-stone-100" />
          <div className="h-1.5 rounded bg-stone-100 w-3/4" />
          <div className="h-1.5 rounded bg-stone-100 w-5/6" />
        </div>
      </div>
    </PhoneMock>
  );
}

export function MiniGarageMock() {
  return (
    <PhoneMock title="Dream Garage" testid="mock-mini-garage">
      <div className="py-2 space-y-2">
        <div className="h-10 rounded-lg bg-gradient-to-br from-stone-200 to-stone-100" />
        <Bar pct={61} color="bg-blue-600" />
        <Bar pct={62} color="bg-emerald-500" />
        <div className="h-1.5 rounded bg-stone-100 w-2/3" />
      </div>
    </PhoneMock>
  );
}
